<script setup>
import { computed } from 'vue'

// Props
// image: optional generic image object used as a fallback for both orientations
const props = defineProps({
  image: {
    type: [Object, Array],
    required: false
  },
  // landscape: optional image object for landscape orientation
  landscape: {
    type: [Object, Array],
    required: false
  },
  // portrait: optional image object for portrait orientation
  portrait: {
    type: [Object, Array],
    required: false
  },
  // Responsive behavior configuration
  // maxWidth: maximum width in pixels for landscape orientation (default: 100vw)
  maxWidth: {
    type: Number,
    required: false,
    default: null
  },
  // maxWidthMobile: maximum width in pixels for portrait orientation (default: 100vw)
  maxWidthMobile: {
    type: Number,
    required: false,
    default: null
  },
  // quality: image quality (1-100), if not set, quality parameter won't be added
  quality: {
    type: Number,
    required: false,
    default: 100
  },
  // loading: native loading behavior for <img>, defaults to lazy (deferral is left to the browser)
  loading: {
    type: String,
    required: false,
    default: 'lazy',
    validator: (value) => ['lazy', 'eager'].includes(value)
  },
  // alt: alternative text for the image (mutually interchangeable with title prop)
  alt: {
    type: String,
    required: false,
    default: null
  },
  // title: title text for the image (mutually interchangeable with alt prop)
  title: {
    type: String,
    required: false,
    default: null
  },
  ariaHidden: {
    type: [Boolean, String],
    required: false,
    default: false
  }
})

const isAriaHidden = computed(() => {
  const v = props.ariaHidden
  if (v === true || v === 'true') return true
  if (v === false || v === 'false' || v === '' || v == null) return false
  return Boolean(v)
})

// Computed
// portraitImage: selected image for portrait orientation
// Priority: portrait > image > landscape
const normalizeAsset = (value) => Array.isArray(value) ? (value?.[0] || {}) : (value || {})
const imageProp = computed(() => normalizeAsset(props.image))
const landscapeProp = computed(() => normalizeAsset(props.landscape))
const portraitProp = computed(() => normalizeAsset(props.portrait))

const portraitImage = computed(() => {
  if (portraitProp.value?.url) return portraitProp.value
  if (imageProp.value?.url) return imageProp.value
  return landscapeProp.value
})

// landscapeImage: selected image for landscape orientation
// Priority: landscape > image > portrait
const landscapeImage = computed(() => {
  if (landscapeProp.value?.url) return landscapeProp.value
  if (imageProp.value?.url) return imageProp.value
  return portraitProp.value
})

// fallbackImage: used by the <img> tag (when no <source> matched)
// Priority: image > landscape > portrait
const fallbackImage = computed(() => {
  if (imageProp.value?.url) return imageProp.value
  if (landscapeProp.value?.url) return landscapeProp.value
  if (portraitProp.value?.url) return portraitProp.value
  return {}
})

// imageText: text for alt and title attributes (mutually interchangeable props alt/title)
// Priority: alt || title > fallbackImage.title > fallbackImage.alt
const imageText = computed(() => {
  if (isAriaHidden.value) return ''
  return props.alt || props.title || fallbackImage.value.title || fallbackImage.value.alt || ''
})

// Imgix helpers
// buildImgixUrl: appends common Imgix params and optional target width and quality.
// SVGs stay SVG — fm=webp / auto=compress rasterizes them into a larger PNG.

function isSvgUrl(url) {
  if (!url) return false
  return /\.svg$/i.test(url.split('?')[0])
}

function svgSrc(baseUrl) {
  // Any Imgix query (including fm=svg) rasterizes the file to PNG.
  return baseUrl.split('?')[0]
}

function buildImgixUrl(baseUrl, width = null, quality = null) {
  if (!baseUrl) return ''
  if (isSvgUrl(baseUrl)) return svgSrc(baseUrl)
  const hasQuery = baseUrl.includes('?')
  const sep = hasQuery ? '&' : '?'
  const widthParam = width ? `&w=${width}` : ''
  const qualityParam = quality !== null ? `&q=${quality}` : '&q=90'
  return `${baseUrl}${sep}fm=webp&fit=max${widthParam}${qualityParam}&dpr=1.4&sharp=5`
}

// buildSrcset: builds a comma-separated srcset from widths
// skipMaxWidth: if true, don't add width parameter for the maximum width (last element)
// skipMaxWidthValue: value to use instead of null when skipMaxWidth is true (e.g., 1552 for landscape)
function buildSrcset(baseUrl, widths, quality = null, skipMaxWidth = false, skipMaxWidthValue = null) {
  if (!baseUrl) return ''
  // One URL is enough — SVG is resolution-independent, and an empty widths
  // list used to fall through to auto=compress (which rasterizes to PNG).
  if (isSvgUrl(baseUrl)) return svgSrc(baseUrl)
  const maxWidth = widths.length > 0 ? Math.max(...widths) : null
  return widths.map((w, index) => {
    const isMaxWidth = skipMaxWidth && w === maxWidth && index === widths.length - 1
    const widthValue = isMaxWidth 
      ? (skipMaxWidthValue !== null ? skipMaxWidthValue : null)
      : w
    const url = buildImgixUrl(baseUrl, widthValue, quality)
    return `${url} ${w}w`
  }).join(', ')
}

// generateWidths: generates responsive widths based on maxWidth
// defaultWidths: array of default widths to use when maxWidth is not provided
function generateWidths(maxWidth, defaultWidths = [375, 480, 768, 1024, 1280, 1440, 1920]) {
  if (!maxWidth) {
    // Return default widths when maxWidth is not provided
    return defaultWidths
  }
  
  // Generate widths: 0.5x, 0.75x, 1x of maxWidth, but not less than 375px
  const widths = []
  const minWidth = 375
  
  // Add smaller widths if maxWidth is large enough
  if (maxWidth >= 768) {
    widths.push(Math.max(minWidth, Math.round(maxWidth * 0.5)))
    widths.push(Math.max(minWidth, Math.round(maxWidth * 0.75)))
  } else if (maxWidth >= 512) {
    widths.push(Math.max(minWidth, Math.round(maxWidth * 0.75)))
  }
  
  // Always add the maxWidth
  widths.push(maxWidth)
  
  // Remove duplicates and sort
  return [...new Set(widths)].sort((a, b) => a - b)
}

// generateSizes: generates sizes attribute based on maxWidth
function generateSizes(maxWidth) {
  if (!maxWidth) {
    return '100vw'
  }
  return `(max-width: ${maxWidth}px) 100vw, ${maxWidth}px`
}

// srcset and sizes for both orientations

const portraitRealImgWidth = computed(() => portraitImage.value?.width || 600)
const landscapeRealImgWidth = computed(() => landscapeImage.value?.width || 1552)

const portraitDefaultWidths = computed(() => {
  const allWidths = [375, 480, 600]
  return allWidths.filter(width => width <= portraitRealImgWidth.value)
})

const landscapeDefaultWidths = computed(() => {
  const allWidths = [375, 480, 768, 1024, 1440, 1920]
  return allWidths.filter(width => width <= landscapeRealImgWidth.value)
})

const portraitSkipMaxWidthValue = computed(() => 600 < portraitRealImgWidth.value ? 600 : null)
const landscapeSkipMaxWidthValue = computed(() => 1552 < landscapeRealImgWidth.value ? 1552 : null)

const portraitWidths = computed(() => generateWidths(props.maxWidthMobile, portraitDefaultWidths.value))
const landscapeWidths = computed(() => generateWidths(props.maxWidth, landscapeDefaultWidths.value))
const portraitSrcset = computed(() => buildSrcset(portraitImage.value?.url, portraitWidths.value, props.quality, !props.maxWidthMobile, !props.maxWidthMobile ? portraitSkipMaxWidthValue.value : null))
const landscapeSrcset = computed(() => buildSrcset(landscapeImage.value?.url, landscapeWidths.value, props.quality, !props.maxWidth, !props.maxWidth ? landscapeSkipMaxWidthValue.value : null))

// fallbackSrc: uses the larger of maxWidth and maxWidthMobile, or default values (1552 for landscape, 600 for portrait) if not set
const fallbackWidth = computed(() => {
  if (props.maxWidth && props.maxWidthMobile) {
    return Math.max(props.maxWidth, props.maxWidthMobile)
  }
  if (props.maxWidth) return props.maxWidth
  if (props.maxWidthMobile) return props.maxWidthMobile
  // When both are not set, use 1552 (landscape default) as it's larger than 600 (portrait default)
  return landscapeSkipMaxWidthValue.value
})
const fallbackSrc = computed(() => buildImgixUrl(fallbackImage.value?.url, fallbackWidth.value, props.quality))

// decoding: sync for eager (critical images), async for lazy (non-blocking)
const decoding = computed(() => props.loading === 'eager' ? 'sync' : 'async')

// fetchpriority: high for eager (critical images), low for lazy (non-critical)
const fetchpriority = computed(() => props.loading === 'eager' ? 'high' : 'low')

const portraitSizes = computed(() => generateSizes(props.maxWidthMobile))
const landscapeSizes = computed(() => generateSizes(props.maxWidth))

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<picture
  :class="{ 'picture--aria-hidden': isAriaHidden }"
>
  <!-- Mobile crop: width, not orientation. A tall desktop window is still desktop. -->
  <source
    v-if="portraitImage?.url"
    media="(max-width: 767px)"
    :srcset="portraitSrcset || (isSvgUrl(portraitImage?.url) ? svgSrc(portraitImage.url) : portraitImage?.url + '?auto=compress&wm=webp&lossless=0&amp;fit=crop')"
    :sizes="portraitSizes"
  />
  <!-- Desktop crop -->
  <source
    v-if="landscapeImage?.url"
    :data-landscape-width="landscapeImage?.width"
    media="(min-width: 768px)"
    :srcset="landscapeSrcset || (isSvgUrl(landscapeImage?.url) ? svgSrc(landscapeImage.url) : landscapeImage?.url + '?auto=compress&wm=webp&lossless=0&amp;fit=crop')"
    :sizes="landscapeSizes"
  />
  <!-- Fallback -->
  <img
    v-if="fallbackImage?.url"
    :src="fallbackSrc"
    :alt="imageText || ''"
    :role="isAriaHidden ? 'presentation' : undefined"
    :aria-hidden="isAriaHidden ? 'true' : undefined"
    :width="fallbackImage.width"
    :height="fallbackImage.height"
    :loading="loading"
    :decoding="decoding"
    :fetchpriority="fetchpriority"
  />
</picture>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.picture--aria-hidden {
  pointer-events: none;
}

img {
  display: block;
  height: 100%;
  width: 100%;
  object-fit: cover;
  object-position: center;
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}

</style>
